import type {
  Currency,
  LoanType,
  MonthlyPayment,
  PriceMode,
  VATRateKey,
} from '@/utils/loan-calculator'
import { formatNumber } from '@/utils/loan-calculator'
import clsx from 'clsx'
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react'
import { useMemo } from 'react'

export default function PaymentSchedule({
  currentVatRate,
  selectedVatRateKey,
  currency,
  initialPayment,
  residualValue,
  monthlyPayments,
  isPaymentScheduleExpanded,
  setIsPaymentScheduleExpanded,
  loanTerm,
  loanType,
  priceMode,
}: {
  currentVatRate: number
  selectedVatRateKey: VATRateKey
  currency: Currency
  initialPayment: string
  residualValue: string
  monthlyPayments: MonthlyPayment[]
  isPaymentScheduleExpanded: boolean
  setIsPaymentScheduleExpanded: React.Dispatch<React.SetStateAction<boolean>>
  loanTerm: string
  loanType: LoanType
  priceMode: PriceMode
}) {
  const paymentsToDisplay = useMemo(() => {
    // If expanded or only a few payments, show all
    if (isPaymentScheduleExpanded || monthlyPayments.length <= 5) {
      return monthlyPayments
    }

    const lastRegularMonth = parseInt(loanTerm)
    const hasInitialPayment = monthlyPayments.some((p) => p.month === 0)
    const hasFinalPayment = monthlyPayments.some(
      (p) => p.month > lastRegularMonth,
    )

    // Find the actual last regular payment (might be different from lastRegularMonth for longer loans)
    const regularPayments = monthlyPayments.filter(
      (p) => p.month > 0 && p.month <= lastRegularMonth,
    )
    const actualLastRegularMonth =
      regularPayments.length > 0
        ? Math.max(...regularPayments.map((p) => p.month))
        : lastRegularMonth

    // Always show: Initial payment (month 0), First month (1), Last regular month, Final payment
    const importantMonths = [
      hasInitialPayment ? 0 : null, // Initial payment (if exists)
      1, // First regular month
      actualLastRegularMonth, // Last regular month
      hasFinalPayment
        ? monthlyPayments.find((p) => p.month > lastRegularMonth)?.month || null
        : null, // Final payment (if exists for open loans)
    ].filter((month) => month !== null) as number[]

    // Return payments for important months, preserving original order
    return monthlyPayments.filter((payment) =>
      importantMonths.includes(payment.month),
    )
  }, [monthlyPayments, isPaymentScheduleExpanded, loanTerm])

  return (
    <div className="overflow-x-auto rounded-xl bg-white p-6 shadow md:p-8">
      <h2 className="mb-6 text-2xl font-bold">Payment Schedule</h2>

      <div className="mb-4 text-sm">
        <p className="text-gray-500">
          VAT Rate:{' '}
          {(currentVatRate * 100).toFixed(
            selectedVatRateKey === 'Custom' ? 2 : 0,
          )}
          %
        </p>
        <p className="text-gray-500">All amounts are in {currency}</p>
        {parseFloat(initialPayment) > 0 && (
          <p className="text-gray-500">
            Initial month represents the initial payment / deposit amount
          </p>
        )}
        {loanType === 'open' && parseFloat(residualValue) > 0 && (
          <p className="text-gray-500">
            The final payment represents the residual value
          </p>
        )}
      </div>

      <div
        className={clsx(
          'mb-4 flex flex-col gap-4 sm:flex-row',
          !isPaymentScheduleExpanded && monthlyPayments.length > 5
            ? 'justify-between'
            : 'justify-end',
        )}
      >
        {!isPaymentScheduleExpanded && monthlyPayments.length > 5 && (
          <p className="mt-1 text-sm text-gray-500">
            <strong>Note:</strong> Showing only key payments (initial, first
            month, last regular month, and final payment)
          </p>
        )}
        {monthlyPayments.length > 5 && (
          <button
            onClick={() =>
              setIsPaymentScheduleExpanded(!isPaymentScheduleExpanded)
            }
            className="flex items-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50"
          >
            {isPaymentScheduleExpanded ? (
              <>
                <span>Show Key Payments Only</span>
                <ChevronsDownUp className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                <span>Show All {monthlyPayments.length} Payments</span>
                <ChevronsUpDown className="ml-2 h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>

      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Month
            </th>
            {priceMode !== 'gross' && (
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Net Principle Payment
              </th>
            )}
            {priceMode !== 'gross' && (
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                VAT
              </th>
            )}
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Gross Principle Payment
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Interest
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Monthly Payment
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Principal Balance
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Using flatMap to create a flat array of rows */}
          {paymentsToDisplay.flatMap((payment, index, array) => {
            // Generate rows array for this payment
            const rows = []

            // Add separator row if needed (when collapsed and there are skipped payments)
            const needsSeparator =
              !isPaymentScheduleExpanded &&
              index > 0 &&
              payment.month - array[index - 1].month > 1

            if (needsSeparator) {
              rows.push(
                <tr
                  key={`separator-${payment.month}`}
                  className="border-b border-gray-100"
                >
                  <td
                    colSpan={priceMode === 'gross' ? 5 : 7}
                    className="px-4 py-3 text-center"
                  >
                    <button
                      onClick={() => setIsPaymentScheduleExpanded(true)}
                      className="mx-auto flex items-center text-sm text-gray-600 transition-colors hover:text-gray-900"
                    >
                      <span className="border-b border-dashed border-gray-400">
                        {monthlyPayments.length - paymentsToDisplay.length} more
                        payments hidden
                      </span>
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                  </td>
                </tr>,
              )
            }

            // Add the payment row
            rows.push(
              <tr
                key={`payment-${payment.month}`}
                className={`border-b border-gray-100 ${
                  payment.month === 0 ||
                  (loanType === 'open' &&
                    payment.month ===
                      Math.max(
                        ...monthlyPayments
                          .filter((p) => p.month > 0)
                          .map((p) => p.month),
                      ) &&
                    payment.month > parseInt(loanTerm))
                    ? 'bg-amber-50'
                    : ''
                }`}
              >
                <td className="whitespace-nowrap px-4 py-3">
                  {payment.month === 0
                    ? 'Initial'
                    : loanType === 'open' &&
                        payment.month ===
                          Math.max(
                            ...monthlyPayments
                              .filter((p) => p.month > 0)
                              .map((p) => p.month),
                          )
                      ? 'Final'
                      : payment.month}
                </td>
                {priceMode !== 'gross' && (
                  <td className="whitespace-nowrap px-4 py-3">
                    {formatNumber(currency, payment.netAmount)}
                  </td>
                )}
                {priceMode !== 'gross' && (
                  <td className="whitespace-nowrap px-4 py-3">
                    {formatNumber(currency, payment.vatAmount)}
                  </td>
                )}
                <td className="whitespace-nowrap px-4 py-3">
                  {formatNumber(currency, payment.grossAmount)}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {formatNumber(currency, payment.interestAmount)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-medium">
                  {formatNumber(currency, payment.monthlyPayment)}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {formatNumber(currency, payment.principalBalance)}
                </td>
              </tr>,
            )

            // Return both rows (the separator if needed + the payment row)
            return rows
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-50">
            <td className="px-4 py-3 font-medium">Total</td>
            {priceMode !== 'gross' && (
              <td className="px-4 py-3 font-medium">
                {formatNumber(
                  currency,
                  monthlyPayments.reduce(
                    (sum, payment) => sum + payment.netAmount,
                    0,
                  ),
                )}
              </td>
            )}
            {priceMode !== 'gross' && (
              <td className="px-4 py-3 font-medium">
                {formatNumber(
                  currency,
                  monthlyPayments.reduce(
                    (sum, payment) => sum + payment.vatAmount,
                    0,
                  ),
                )}
              </td>
            )}
            <td className="px-4 py-3 font-medium">
              {formatNumber(
                currency,
                monthlyPayments.reduce(
                  (sum, payment) => sum + payment.grossAmount,
                  0,
                ),
              )}
            </td>
            <td className="px-4 py-3 font-medium">
              {formatNumber(
                currency,
                monthlyPayments.reduce(
                  (sum, payment) => sum + payment.interestAmount,
                  0,
                ),
              )}
            </td>
            <td className="px-4 py-3 font-medium">
              {formatNumber(
                currency,
                monthlyPayments.reduce(
                  (sum, payment) => sum + payment.monthlyPayment,
                  0,
                ),
              )}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
